import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, LogOut } from "lucide-react";
import { ChatIcon, SettingsIcon, UserUpdateIcon, UserVoiceIcon } from "../../assets/icon";
import ChatBlue from '../../assets/images/chat-blue.png'
import ChatNeutral from '../../assets/images/chat-neutral.png'
import useRequest from "../hook/use-req";
import { UserResponse } from "../types";

export const TopNav: React.FC<{ onMenuClick?: () => void }> = ({ onMenuClick }) => {
  const token = localStorage.getItem("authToken");
  const navigate = useNavigate();
  const [user, setUser] = useState<UserResponse | null>(null);
  const [orgMenuOpen, setOrgMenuOpen] = useState(false);
  const orgMenuRef = useRef<HTMLDivElement>(null);
  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getUserData();
      setUser(response);
    };
    fetchData();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (orgMenuRef.current && !orgMenuRef.current.contains(e.target as Node)) {
        setOrgMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Same localStorage-clear + redirect-to-login convention as every other
  // sidebar/topbar's own Log Out button (e.g. cxo-sidebar/cxo-topbar.tsx).
  const handleSignOut = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("userDetails");
    localStorage.removeItem("resetOtpCode");
    localStorage.removeItem("role");
    navigate("/");
  };

  return (
    <header className="sticky bg-white dark:bg-gray-900 mb-3 px-3 rounded-lg top-0 flex items-center justify-between gap-2 w-full py-3 z-20">
      <div className="flex items-center gap-2 min-w-0">
        {onMenuClick && (
          <button
            onClick={onMenuClick}
            aria-label="Toggle menu"
            className="md:hidden shrink-0 p-2 -ml-1 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <Menu size={20} />
          </button>
        )}
        {/* Welcome pill */}
        <div className="flex items-center gap-2 bg-[#FDFBF2] dark:bg-gray-800 border-2 border-[#000] dark:border-gray-600 rounded-full px-4 py-2 min-w-0">
          <span className="flex items-center shrink-0">
            <img src={ChatNeutral} alt="ChatNeutral" />
          </span>
          <span className="text-gray-900 dark:text-gray-100 text-sm truncate hidden sm:inline">
            Welcome, <strong>{user?.payload?.first_name}</strong>. Get stuff done faster with ConvoHubAI
          </span>
          <span className="text-gray-900 dark:text-gray-100 text-sm truncate sm:hidden">
            Welcome, <strong>{user?.payload?.first_name}</strong>
          </span>
        </div>
      </div>

      {/* Right side buttons */}
      <div className="flex items-center gap-2 shrink-0">
        <button className="flex items-center gap-2 bg-[#FFFFFF] dark:bg-gray-800 rounded-xl px-3 sm:px-4 py-2 text-[14px] text-[#3A3428] dark:text-gray-200 font-medium hover:bg-violet-100 dark:hover:bg-gray-700 transition-colors">
          <img src={ChatNeutral} alt="ChatBlue" />
          <span className="hidden sm:inline">ConvoAI</span>
        </button>

        <button className="flex items-center gap-2 bg-[#FFFFFF] dark:bg-gray-800 text-[#3A3428] dark:text-gray-200 rounded-xl px-3 sm:px-4 py-2 text-[14px] font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
          <UserVoiceIcon />
          <span className="hidden sm:inline">Talk to Us</span>
        </button>

        <div className="relative shrink-0" ref={orgMenuRef}>
          <button
            onClick={() => setOrgMenuOpen((o) => !o)}
            className="flex items-center gap-2 bg-[#FFFFFF] dark:bg-gray-800 rounded-xl pl-2 pr-3 py-2 text-[14px] text-[#3A3428] dark:text-gray-200 font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            <img className="w-5 h-5 shrink-0" src={user?.payload?.org_logo} alt="logo" />
            <span className="hidden sm:inline truncate max-w-[120px]">{user?.payload?.org_name}</span>
            <svg
              width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"
              className={`shrink-0 transition-transform text-black dark:text-gray-300 ${orgMenuOpen ? "rotate-180" : ""}`}
            >
              <path d="M6 9L12 15L18 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          {orgMenuOpen && (
            <div className="absolute right-0 z-30 mt-1 w-44 overflow-hidden rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg py-1">
              <button
                onClick={() => { setOrgMenuOpen(false); navigate("/settings"); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-[#3A3428] dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 border-none cursor-pointer text-left"
              >
                <SettingsIcon className="w-4 h-4 shrink-0" />
                Settings
              </button>
              <button
                onClick={() => { setOrgMenuOpen(false); navigate("/convoAi"); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-[#3A3428] dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 border-none cursor-pointer text-left"
              >
                <ChatIcon className="w-4 h-4 shrink-0" />
                ConvoAI
              </button>
              <button
                onClick={() => { setOrgMenuOpen(false); handleSignOut(); }}
                className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-[#3A3428] dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 border-none cursor-pointer text-left"
              >
                <LogOut className="w-4 h-4 shrink-0" />
                Log Out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};